// Thin client for the Heartline license worker's /admin/* API.
//
// Auth model: in production, the worker sits behind Cloudflare Access
// and the browser carries Access cookies automatically — we just need
// to set credentials:"include" on every fetch. For local dev where
// Cf Access is not configured, the worker accepts an
// `Authorization: Bearer <ADMIN_TOKEN>` header where ADMIN_TOKEN
// matches the worker's secret (set via `wrangler secret put`).
//
// The admin URL base comes from `VITE_WORKER_URL`. In `npm run dev`
// the vite proxy rewrites `/admin/*` to that URL transparently so
// requests stay same-origin (otherwise Cf Access cookies wouldn't
// flow back to the browser).
//
// Token format minted by the worker:
//   HRTL.<base64url(payload)>.<base64url(ed25519_sig)>
//   payload = "1|entity|tier|maxMachines|issuedAtSec|expirySec"
//   tier ∈ single | network | unlimited
//   expirySec = 0 → perpetual

/** A record in the developer ledger. These have no effect on validation —
 *  the plugin never calls this worker; they are a human reference only. */
export interface LicenseRecord {
  key: string;
  entity: string;
  tier: string;
  notes: string;
  created_at?: number;
  _corrupt?: boolean;
}

/** Result returned by POST /admin/mint. The `token` is the signed string
 *  to send to the buyer. */
export interface MintResult {
  token: string;
  entity: string;
  tier: string;
  maxMachines: number;
  expiryEpochSec: number;
}

/** Request body for POST /admin/mint. */
export interface MintRequest {
  entity: string;
  tier: "single" | "network" | "unlimited";
  maxMachines: number;
  /** 0 = perpetual. */
  expiryEpochSec: number;
}

/** Global developer notes stored in KV `__global__`. Not a kill switch. */
export interface GlobalConfig {
  notes: string;
  updated_at?: number;
}

export type BugStatus = "open" | "ack" | "fixed" | "wontfix";

/** An operator bug report submitted by the plugin's Studio "Report a bug" form
 *  (stored in KV under `bug::<id>`). The plugin self-reports entity/tier/machine
 *  etc.; `licenseKey` is the bearer the worker recorded for cross-reference. */
export interface TicketComment {
  author: "admin" | "operator";
  body: string;
  ts: number;
}

export interface BugReport {
  id: string;
  category: string;
  description: string;
  entity: string;
  tier: string;
  licenseState: string;
  machine: string;
  pluginVersion: string;
  sentAt: number | null;
  licenseKey: string;
  receivedAt: number;
  status: BugStatus;
  notes: string;
  comments?: TicketComment[];
  updated_at?: number;
  /** True when the operator has posted (new ticket or comment) since you last opened it. */
  unread?: boolean;
  /** Attached signed hotfix JAR, if any. */
  build?: BuildAttachment;
  _corrupt?: boolean;
}

export interface BuildAttachment {
  filename: string;
  size: number;
  sha256: string;
  signature: string;
  uploadedAt: number;
}

const TOKEN_KEY = "heartline.admin.devtoken";

export function getDevToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}
export function setDevToken(t: string | null): void {
  if (t === null || t === "") localStorage.removeItem(TOKEN_KEY);
  else localStorage.setItem(TOKEN_KEY, t);
}

export class AdminApiError extends Error {
  status: number;
  payload: unknown;
  constructor(status: number, message: string, payload: unknown) {
    super(message);
    this.status = status;
    this.payload = payload;
  }
}

/** Absolute base for /admin + /api calls. Production (Cloudflare Pages) has no
 *  dev proxy, so requests must hit the worker's ABSOLUTE URL (VITE_WORKER_URL);
 *  a relative /admin path hits the Pages host instead (SPA fallback → 200 HTML
 *  on GET, 405 on PUT). In dev, base is "" and vite's proxy rewrites /admin →
 *  the worker (same-origin, so Cf Access cookies flow). */
function apiBase(): string {
  return import.meta.env.PROD ? (import.meta.env.VITE_WORKER_URL ?? "") : "";
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers["Content-Type"] = "application/json";
  const devToken = getDevToken();
  if (devToken) headers["Authorization"] = `Bearer ${devToken}`;
  const res = await fetch(apiBase() + path, {
    method,
    headers,
    credentials: "include",  // Cloudflare Access cookies
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  let parsed: unknown = null;
  if (text.length > 0) {
    try { parsed = JSON.parse(text); } catch { parsed = text; }
  }
  if (!res.ok) {
    // A 401 means the stored token is stale/wrong — tell the shell to drop it
    // and re-show the token gate instead of every page toasting "unauthorized".
    if (res.status === 401) window.dispatchEvent(new Event("hl-unauthorized"));
    const msg =
      (parsed && typeof parsed === "object" && "error" in parsed && typeof (parsed as { error: unknown }).error === "string")
        ? (parsed as { error: string }).error
        : res.statusText;
    throw new AdminApiError(res.status, msg, parsed);
  }
  return parsed as T;
}

export const adminApi = {
  /** Fetch the full developer ledger. */
  list:   ()                                       => request<{ keys: LicenseRecord[] }>("GET",    "/admin/keys"),
  /** Mint a new signed token. Returns the token + echoed fields. */
  mint:   (body: MintRequest)                      => request<MintResult>("POST",  "/admin/mint", body),
  /** Update notes (or other mutable ledger fields) for a key record. */
  update: (key: string, patch: Partial<LicenseRecord>)      => request<LicenseRecord>("PATCH",  `/admin/keys/${encodeURIComponent(key)}`, patch),
  /** Delete a ledger record. Has no effect on the token itself. */
  remove: (key: string)                            => request<{ ok: true; key: string }>("DELETE", `/admin/keys/${encodeURIComponent(key)}`),
  getGlobal: ()                                    => request<GlobalConfig>("GET", "/admin/global"),
  putGlobal: (cfg: GlobalConfig)                   => request<GlobalConfig>("PUT", "/admin/global", cfg),
};

export const bugReportApi = {
  /** List every operator bug report, newest first. */
  list:   ()                                       => request<{ reports: BugReport[] }>("GET", "/admin/bug-reports"),
  /** Update a report's triage status and/or notes. */
  update: (id: string, patch: { status?: BugStatus; notes?: string }) =>
    request<BugReport>("PATCH", `/admin/bug-reports/${encodeURIComponent(id)}`, patch),
  /** Delete a report. */
  remove: (id: string)                             => request<{ ok: true; id: string }>("DELETE", `/admin/bug-reports/${encodeURIComponent(id)}`),
  /** Post an admin comment to a ticket's thread (visible to the operator). */
  comment: (id: string, body: string)             => request<BugReport>("POST", `/admin/tickets/${encodeURIComponent(id)}/comments`, { body }),
  /** Mark a ticket read (clears its "new message" dot). Called on drawer open. */
  markRead: (id: string)                          => request<BugReport>("POST", `/admin/tickets/${encodeURIComponent(id)}/read`),
  /** Attach a signed hotfix JAR. `signature` is the base64 from `sign-jar.mjs`. */
  uploadBuild: async (id: string, file: File, signature: string): Promise<BugReport> => {
    const headers: Record<string, string> = { "X-Filename": file.name, "X-Signature": signature };
    const dev = getDevToken();
    if (dev) headers["Authorization"] = `Bearer ${dev}`;
    const res = await fetch(`${apiBase()}/admin/tickets/${encodeURIComponent(id)}/build`, {
      method: "PUT", headers, credentials: "include", body: await file.arrayBuffer(),
    });
    const text = await res.text();
    let parsed: unknown = null;
    if (text) { try { parsed = JSON.parse(text); } catch { parsed = text; } }
    if (!res.ok) {
      const msg = (parsed && typeof parsed === "object" && "error" in parsed && typeof (parsed as { error: unknown }).error === "string")
        ? (parsed as { error: string }).error : res.statusText;
      throw new AdminApiError(res.status, msg, parsed);
    }
    return parsed as BugReport;
  },
  /** Remove the attached hotfix build. */
  deleteBuild: (id: string)                        => request<BugReport>("DELETE", `/admin/tickets/${encodeURIComponent(id)}/build`),
};
